import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Shown at the top of every page when a SUPER_ADMIN is impersonating a tenant.
 * Read+write access (per choice 3b). Persistent until "Quitter" is clicked (4b).
 */
export default function ImpersonationBanner() {
  const { impersonating, impersonatedTenant, stopImpersonation } = useAuth() || {};
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!impersonating || !impersonatedTenant) return null;

  const quit = async () => {
    setBusy(true);
    try {
      await stopImpersonation();
      toast.success("Impersonation terminée");
      navigate("/admin");
    } catch (e) {
      toast.error("Impossible de quitter l'impersonation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-2
                  border-b border-amber-700/40 bg-amber-500 px-4 py-2 text-amber-950"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2 text-xs font-medium">
        <ShieldAlert className="h-4 w-4" />
        <span className="font-mono uppercase tracking-widest">Impersonation active</span>
        <span className="hidden text-amber-950/80 sm:inline">·</span>
        <span data-testid="impersonation-banner-tenant">
          {impersonatedTenant.business_name} <span className="font-mono opacity-70">/{impersonatedTenant.slug}</span>
        </span>
        <span className="hidden text-amber-950/80 sm:inline">·</span>
        <span className="hidden text-[11px] text-amber-950/70 sm:inline">
          (lecture + écriture — toute action est journalisée)
        </span>
      </div>
      <button
        type="button"
        onClick={quit}
        disabled={busy}
        className="inline-flex items-center gap-1 border border-amber-900/30 bg-amber-100 px-3 py-1
                    text-xs font-bold uppercase tracking-widest text-amber-900
                    transition-colors hover:bg-amber-50 disabled:opacity-50"
        data-testid="impersonation-banner-quit"
      >
        <LogOut className="h-3 w-3" />
        {busy ? "Sortie…" : "Quitter"}
      </button>
    </div>
  );
}
